// CustomerForm.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useUser } from '@clerk/clerk-react';
import AccountFormService from '../../../service/utils/accountFormService';
import { toast } from 'sonner';
import { useLoader } from '../../../service/Hooks/useLoader';

// Import des composants
import FormHeader from './Components/FormHeader';
import FormStepper from './Components/FormStepper';
import AccountTypePage from './Components/AccountTypePage';
import PersonalInfoPage from './Components/PersonalInfoPage';
import CompanyInfoPage from './Components/CompanyInfoPage';
import DocumentsPage from './Components/DocumentsPage';
import SummaryPage from './Components/SummaryPage';
import { useFormNavigation } from '../../../service/Hooks/useFormNavigation';
import ManagedAccountSection from './Components/ManagedAccountSection';
import { getAccountForUpdate } from '../../../service/api/accountService';
import { useUserData } from '../../../service/Hooks/useUserData';

const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const CustomerForm = ({ initialAccountData, onCreateSuccess, isEditMode = false, onEditSuccess, show = false, onHide }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, getValues, control } = useForm({
    defaultValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      preference: '',
      phoneNumber: '',
      address: '',
      job: '',
      countryOfBirth: '',
      cityOfBirth: '',
      yearlyRevenue: 0,
      capital: 0,
      totalOutstandingShares: 0,
      legalForm: '',
      isin: '',
      tva: '',
      cash: 0,
      description: '',
      company_adress: '',
      typeId: '',
      validationStatus: 0,
      subscription: false,
      allocation: false,
      transit: false,
      restriction: false,
      consignment: false,
      domainId: '',
      dob: '',
      numeroCompteBancaire: '',
      numeroIdentificationFiscale: '',
      IsManagedAccount: false,
      RepresentativeAccountIds: [],
      MandateProofFiles: [],
      MandateTypes: [],
      RelationshipTypes: []
    }
  });
  
  // États existants
  const [existingFiles, setExistingFiles] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(!!initialAccountData || isEditMode);
  const [showModal, setShowModal] = useState(show);
  const [formInitialized, setFormInitialized] = useState(false);
  const modalRef = useRef(null);
  const [domains, setDomains] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const { user } = useUser();
  const [requiredFields, setRequiredFields] = useState([]);
  const [backendErrors, setBackendErrors] = useState({});
  const { showLoader, hideLoader } = useLoader();
  const [isManagedAccount, setIsManagedAccount] = useState(false);
  const [isMinor, setIsMinor] = useState(false);
  const [nonManagedAccounts, setNonManagedAccounts] = useState([]);
  const [validEmail, setValidEmail] = useState(false);
  const [validDob, setValidDob] = useState(false);
  const [customErrors, setCustomErrors] = useState({});

  const {
    role,
    domains: userDomains, 
    selectedDomain,
    loading: userLoading,
    accountId: connectedAccountId, 
    isAdmin
  } = useUserData();

  // Navigation multi-pages
  const steps = [
    { label: 'Type de Compte' },
    { label: 'Informations Personnelles' },
    { label: 'Informations Entreprise' },
    { label: 'Documents' },
    { label: 'Récapitulatif' }
  ];

  const {
    currentPage,
    nextPage,
    prevPage,
    goToPage,
    visitedPages,
    canGoNext,
    canGoPrev,
    isLastPage,
    pageErrors,
    setErrorsForPage,
    hasPageErrors
  } = useFormNavigation(steps.length, watch("typeId"));

  // Surveiller le changement de IsManagedAccount
  const watchedIsManagedAccount = watch("IsManagedAccount");
  useEffect(() => {
    setIsManagedAccount(watchedIsManagedAccount);
  }, [watchedIsManagedAccount]);

  // Fonction pour réinitialiser complètement le formulaire
  const resetForm = useCallback(() => {
    reset({
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      preference: '',
      phoneNumber: '',
      address: '',
      job: '',
      countryOfBirth: '',
      cityOfBirth: '',
      yearlyRevenue: 0,
      capital: 0,
      totalOutstandingShares: 0,
      legalForm: '',
      isin: '',
      tva: '',
      cash: 0,
      description: '',
      company_adress: '',
      typeId: '',
      validationStatus: 0,
      subscription: false,
      allocation: false,
      transit: false,
      restriction: false,
      consignment: false,
      domainId: '',
      dob: '',
      numeroCompteBancaire: '',
      numeroIdentificationFiscale: '',
      IsManagedAccount: false,
      RepresentativeAccountIds: [],
      MandateProofFiles: [],
      MandateTypes: [],
      RelationshipTypes: []
    });
    setExistingFiles({});
    setFormInitialized(false);
    setIsUpdateMode(false);
    setBackendErrors({});
    setCustomErrors({});
    setIsManagedAccount(false);
  }, [reset]);

  // Chargement des données initiales
  useEffect(() => {
    async function fetchData() {
      try {
        setDomains(await AccountFormService.fetchDomains());
        setAccountTypes(await AccountFormService.fetchAccountTypes());
        
        // Si mode édition, charger les données du compte
        if (isEditMode && initialAccountData?.id && !formInitialized) {
          await fetchAccountForEdit(initialAccountData.id);
        }
      } catch (error) {
        // Error handled in service
      }
    }
    
    if (user && !userLoading) { 
      fetchData();
    }
  }, [user, isEditMode, initialAccountData, formInitialized, userLoading]);

  // Mettre à jour le domaine quand le rôle est manager
  useEffect(() => {
    if (role === "manager" && selectedDomain) {
      setValue("domainId", selectedDomain.id);
    } else if (isAdmin) {
      // Pour les admins, laisser le champ vide pour qu'ils choisissent
      setValue("domainId", '');
    }
  }, [role, selectedDomain, setValue, isAdmin]);

  // Surveille la sélection du type de compte
  const selectedTypeId = watch("typeId");
  const selectedAccountType = accountTypes.find(at => at.id === selectedTypeId);

  // Met à jour les champs requis quand le type de compte change
  useEffect(() => {
    if (selectedAccountType) {
      const fields = AccountFormService.getRequiredFieldsForType(selectedAccountType.name);
      setRequiredFields(fields);
    } else {
      setRequiredFields([]);
    }
  }, [selectedAccountType]);

  // Vérifie si un type est choisi
  const isTypeChosen = !!selectedAccountType;
  const isParticulier = isTypeChosen && selectedAccountType.name.toLowerCase() === "particulier";
  const isEntrepriseType = isTypeChosen && [
    "entreprise", "assurance", "banques", "association", "autres entreprises", 'autres', 'opc', 'opcvm', 'fia'
  ].includes(selectedAccountType.name.toLowerCase());
  
  const personalDisabled = !isTypeChosen || isEntrepriseType;
  const companyDisabled = !isTypeChosen || isParticulier;
  const commonDisabled = !isTypeChosen;

  // Fonction pour charger les données en mode édition
  const fetchAccountForEdit = async (accountId) => {
    setIsLoading(true);
    try {
      const accountData = await getAccountForUpdate(accountId);
      resetForm();
      await populateForm(accountData);
      
      setExistingFiles({
        pictureUrl: accountData.pictureUrl,
        proofOfAddressUrl: accountData.proofOfAddressUrl,
        proofOfRevenueUrl: accountData.proofOfRevenueUrl,
        signatureImageUrl: accountData.signatureImageUrl,
        companyLogoUrl: accountData.companyLogoUrl,
        cniUrl: accountData.cniUrl,
        rccmUrl: accountData.rccmUrl,
        procurationUrl: accountData.procurationUrl,
        attestationImmatriculationUrl: accountData.attestationImmatriculationUrl,
        statutUrl: accountData.statutUrl,
        annonceLegaleUrl: accountData.annonceLegaleUrl,
        photoIdentiteUrl: accountData.photoIdentiteUrl,
        journalAnnoncesLegalesUrl: accountData.journalAnnoncesLegalesUrl,
        referenceBancaireEntrepriseUrl: accountData.referenceBancaireEntrepriseUrl,
        banqueDomiciliationUrl: accountData.banqueDomiciliationUrl,
        formulaire_OuvertureUrl: accountData.formulaire_OuvertureUrl,
        convention_OuvertureUrl: accountData.convention_OuvertureUrl,
        kycUrl: accountData.kycUrl,
        pictureFileName: accountData.pictureFileName,
        proofOfAddressFileName: accountData.proofOfAddressFileName,
        proofOfRevenueFileName: accountData.proofOfRevenueFileName,
        signatureImageFileName: accountData.signatureImageFileName,
        companyLogoFileName: accountData.companyLogoFileName,
        cniFileName: accountData.cniFileName,
        rccmFileName: accountData.rccmFileName,
        procurationFileName: accountData.procurationFileName,
        attestationImmatriculationFileName: accountData.attestationImmatriculationFileName,
        statutFileName: accountData.statutFileName,
        annonceLegaleFileName: accountData.annonceLegaleFileName,
        photoIdentiteFileName: accountData.photoIdentiteFileName,
        journalAnnoncesLegalesFileName: accountData.journalAnnoncesLegalesFileName,
        referenceBancaireEntrepriseFileName: accountData.referenceBancaireEntrepriseFileName,
        banqueDomiciliationFileName: accountData.banqueDomiciliationFileName,
        formulaire_OuvertureFileName: accountData.formulaire_OuvertureFileName,
        convention_OuvertureFileName: accountData.convention_OuvertureFileName,
        kycFileName: accountData.kycFileName
      });

      setFormInitialized(true);
      setIsUpdateMode(true);
      
    } catch (error) {
      toast.error("Desole, une erreur s'est produite lors du chargement des données du compte");
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour peupler le formulaire
  const populateForm = async (accountData) => {
    return new Promise((resolve) => {
      const fields = [
        'id', 'companyName', 'firstName', 'lastName', 'email', 'preference', 
        'phoneNumber', 'address', 'job', 'countryOfBirth', 'cityOfBirth',
        'yearlyRevenue', 'capital', 'totalOutstandingShares', 'legalForm', 
        'isin', 'tva', 'cash', 'description', 'company_adress', 'typeId', 'validationStatus',
        'numeroCompteBancaire', 'numeroIdentificationFiscale', 'IsManagedAccount'
      ];
      
      fields.forEach(field => {
        if (accountData[field] !== undefined && accountData[field] !== null) {
          setValue(field, accountData[field], { shouldDirty: true, shouldValidate: true });
        }
      });
      
      const booleanFields = ['subscription', 'allocation', 'transit', 'restriction', 'consignment'];
      booleanFields.forEach(field => {
        setValue(field, !!accountData[field], { shouldDirty: true, shouldValidate: true });
      });
      
      if (accountData.dob) {
        const dobDate = new Date(accountData.dob);
        setValue('dob', dobDate.toISOString().split('T')[0], { shouldDirty: true, shouldValidate: true });
      }
      
      if (accountData.domainId) {
        setValue('domainId', accountData.domainId, { shouldDirty: true, shouldValidate: true });
      }
      
      setExistingFiles(prev => ({
        ...prev,
        ...accountData
      }));

      const fileFields = [
        'picture', 'companyLogo', 'proofOfAddress', 'proofOfRevenue', 
        'signatureImage', 'cni', 'rccm', 'statut', 'annonceLegale', 
        'procuration', 'attestationImmatriculation', 'photoIdentite',
        'journalAnnoncesLegales', 'referenceBancaireEntreprise', 'banqueDomiciliation','formulaire_Ouverture','convention_Ouverture','kyc'
      ];

      fileFields.forEach(field => {
        const urlField = `${field}Url`;
        const fileNameField = `${field}FileName`;
        if (accountData[urlField]) {
          setExistingFiles(prev => ({
            ...prev,
            [urlField]: accountData[urlField],
            [fileNameField]: accountData[fileNameField] || accountData[urlField]?.split('/').pop()
          }));
        }
      });
      
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  const handleRemoveFile = async (fieldName) => {
    if (!isEditMode || !initialAccountData?.id) {
      toast.error("Impossible de supprimer le fichier : données manquantes");
      return;
    }
    
    await AccountFormService.handleRemoveFile(
      initialAccountData.id,
      fieldName,
      setExistingFiles,
      showLoader,
      hideLoader
    );
  };

  const isFieldRequired = (fieldName) => {
    const values = getValues();
    const dob = values.dob;
    const isManagedAccount = values.IsManagedAccount || false;
    const hasRepresentative = values.RepresentativeAccountIds && 
      values.RepresentativeAccountIds.length > 0;
    
    let isMinorWithRepresentative = false;
    if (dob && isManagedAccount && hasRepresentative) {
      const age = calculateAge(dob);
      if (age !== null && age < 18) {
        isMinorWithRepresentative = true;
      }
    }
    
    if (isMinorWithRepresentative) {
      const notRequiredForMinorsWithRepresentative = [
        'cni',
        'proofOfAddress',
        'numeroIdentificationFiscale'
      ];
      
      if (notRequiredForMinorsWithRepresentative.includes(fieldName)) {
        return false;
      }
    }
    
    return requiredFields.includes(fieldName);
  };

  const shouldShowField = (fieldName) => {
    return AccountFormService.shouldShowField(
      fieldName, 
      selectedAccountType?.name, 
      isParticulier, 
      isEntrepriseType
    );
  };

  const validateCurrentPage = () => {
    const values = getValues();
    const currentErrors = [];
    
    switch (currentPage) {
      case 0:
        if (!values.typeId) {
          currentErrors.push({ field: 'typeId', message: 'Veuillez sélectionner un type de compte' });
        }
        if (isAdmin && !isUpdateMode && !values.domainId) {
          currentErrors.push({ field: 'domainId', message: 'Veuillez sélectionner un domaine' });
        }
        break;
        
      case 1:
        if (!isManagedAccount && isFieldRequired('email') && (!values.email || customErrors.email)) {
          currentErrors.push({ field: 'email', message: customErrors.email || 'L\'email est requis' });
        }
        if (isFieldRequired('phoneNumber') && !values.phoneNumber) {
          currentErrors.push({ field: 'phoneNumber', message: 'Le numéro de téléphone est requis' });
        }
        if (isFieldRequired('firstName') && !values.firstName) {
          currentErrors.push({ field: 'firstName', message: 'Le prénom est requis' });
        }
        if (isFieldRequired('lastName') && !values.lastName) {
          currentErrors.push({ field: 'lastName', message: 'Le nom est requis' });
        }
        if (isFieldRequired('dob') && (!values.dob || customErrors.dob)) {
          currentErrors.push({ field: 'dob', message: customErrors.dob || 'La date de naissance est requise' });
        }
        if (isFieldRequired('address') && !values.address) {
          currentErrors.push({ field: 'address', message: 'L\'adresse est requise' });
        }
        if (isFieldRequired('cityOfBirth') && !values.cityOfBirth) {
          currentErrors.push({ field: 'cityOfBirth', message: 'La ville de Naissance est requise' });
        }
        if (isFieldRequired('countryOfBirth') && !values.countryOfBirth) {
          currentErrors.push({ field: 'countryOfBirth', message: 'Le Pays de Naissance est requis' });
        }
        
        if (values.dob) {
          const age = calculateAge(values.dob);
          if (age !== null && age < 18) {
            if (!values.IsManagedAccount) {
              currentErrors.push({
                field: 'dob',
                message: 'Pour les mineurs, vous devez activer "Compte Administré" et ajouter un représentant légal'
              });
            }
          }
        }
        break;
        
      case 2:
        if (isEntrepriseType) {
          if (isFieldRequired('companyName') && !values.companyName) {
            currentErrors.push({ field: 'companyName', message: 'Le nom de l\'entreprise est requis' });
          }
          if (isFieldRequired('company_adress') && !values.company_adress) {
            currentErrors.push({ field: 'company_adress', message: 'L\'adresse de l\'entreprise est requise' });
          }
          if (isFieldRequired('legalForm') && !values.legalForm) {
            currentErrors.push({ field: 'legalForm', message: 'La forme juridique est requise' });
          }
          if (isFieldRequired('capital') && (!values.capital || values.capital <= 0)) {
            currentErrors.push({ field: 'capital', message: 'Le capital est requis et doit être supérieur à 0' });
          }
        }

        if (values.IsManagedAccount) {
          const representativeIds = values.RepresentativeAccountIds || [];
          const mandateTypes = values.MandateTypes || [];
          const relationshipTypes = values.RelationshipTypes || [];
          const mandateProofFiles = values.MandateProofFiles || [];
          
          const isMinor = () => {
            if (!values.dob) return false;
            const age = calculateAge(values.dob);
            return age !== null && age < 18;
          };
          
          if (isMinor() && representativeIds.length === 0) {
            currentErrors.push({
              field: 'representativeAccountIds',
              message: 'Au moins un mandataire est requis pour les mineurs avec compte administré'
            });
          }
          
          representativeIds.forEach((id, index) => {
            if (!id) currentErrors.push({ field: `representativeAccountIds[${index}]`, message: 'Veuillez sélectionner un compte mandataire' });
            if (!mandateTypes[index]) currentErrors.push({ field: `MandateTypes[${index}]`, message: 'Le type de mandat est requis' });
            if (!relationshipTypes[index]) currentErrors.push({ field: `RelationshipTypes[${index}]`, message: 'Le type de relation est requis' });
            if (!mandateProofFiles[index]) currentErrors.push({ field: `MandateProofFiles[${index}]`, message: 'La preuve du mandat est requise' });
          });
        }
        break;
        
      case 3: {
        // Liste de tous les documents possibles
        const documentFields = [
          'picture', 'photoIdentite', 'cni', 'proofOfAddress', 'proofOfRevenue',
          'journalAnnoncesLegalesFile', 'banqueDomiciliationFile', 'referenceBancaireEntrepriseFile',
          'statut', 'attestationImmatriculation', 'annonceLegale', 'procuration',
          'convention_Ouverture', 'formulaire_Ouverture', 'kyc'
        ];
        
        documentFields.forEach(field => {
          // isFieldRequired va utiliser notre nouveau AccountFormService où proofOfAddress n'est plus requis
          if (isFieldRequired(field)) {
            const hasExistingFile = existingFiles[`${field}Url`];
            const hasNewFile = values[field] && values[field].length > 0;
            
            if (!hasExistingFile && !hasNewFile) {
              currentErrors.push({
                field: field,
                message: `Le document ${getDocumentLabel(field)} est requis`
              });
            }
          }
        });
        break;
      }
    }
    
    setErrorsForPage(currentPage, currentErrors);
    return currentErrors.length === 0;
  };

  const getDocumentLabel = (fieldName) => {
    const labels = {
      picture: "Photo d'identité 1",
      photoIdentite: "Photo d'identité 2", 
      cni: "CNI",
      proofOfAddress: "Justificatif de domicile",
      proofOfRevenue: "Justificatif de revenu",
      journalAnnoncesLegalesFile: "Procès-verbal du conseil d'administration",
      banqueDomiciliationFile: "Procès-verbal de l'assemblée générale constitutive",
      referenceBancaireEntrepriseFile: "Référence bancaire",
      statut: "Statut",
      attestationImmatriculation: "Attestation d'immatriculation",
      annonceLegale: "Annonce légale",
      procuration: "Procuration"
    };
    return labels[fieldName] || fieldName;
  };

  const handleNext = () => {
    if (validateCurrentPage()) {
      nextPage();
    } else {
      const errorCount = pageErrors[currentPage]?.length || 0;
      toast.error(`Veuillez corriger ${errorCount} erreur(s) avant de continuer`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const hasError = (fieldName) => {
    return pageErrors[currentPage]?.some(error => error.field === fieldName) || false;
  };

  const getErrorMessage = (fieldName) => {
    const error = pageErrors[currentPage]?.find(error => error.field === fieldName);
    return error ? error.message : null;
  };

  const handlePrev = () => {
    prevPage();
  };

  const onSubmit = async (data) => {
    if (!isLastPage) {
      handleNext();
      return;
    }
  };

  const handleFinalSubmit = async () => {
    const data = getValues();
    
    if (data.dob && !data.IsManagedAccount) {
      const age = calculateAge(data.dob);
      if (age !== null && age < 18) {
        toast.error(
          <div>
            <strong>Représentant légal requis !</strong>
            <div className="small">
              Les personnes de moins de 18 ans doivent avoir un représentant légal.
              <br/>
              <button 
                type="button"
                className="btn btn-sm btn-primary mt-2"
                onClick={() => {
                  setValue("IsManagedAccount", true);
                  goToPage(1);
                  setTimeout(() => {
                    document.querySelector('.card.border-primary')?.scrollIntoView({ 
                      behavior: 'smooth' 
                    });
                  }, 100);
                }}
              >
                <i className="fas fa-user-shield me-1"></i>
                Activer le mode compte administré
              </button>
            </div>
          </div>,
          { duration: 10000 }
        );
        return;
      }
    }
    
    setBackendErrors({});
    
    showLoader({ 
      message: isUpdateMode ? "Mise à jour du compte en cours..." : "Création du compte en cours...", 
      indeterminate: true,
      nonBlocking: true
    });
      
    try {
      const response = await AccountFormService.submitForm(
        data, 
        isUpdateMode, 
        initialAccountData, 
        user, 
        role, 
        resetForm, 
        setBackendErrors,
        accountTypes,
        existingFiles,
        connectedAccountId,
        selectedDomain 
      );
      
      hideLoader();
      
      if (isUpdateMode) {
        toast.success("Compte modifié avec succès !");
        if (onEditSuccess) onEditSuccess(response);
      } else {
        toast.success("Compte créé avec succès !");
        if (onCreateSuccess) onCreateSuccess();
      }
      
      const modalElement = modalRef.current;
      if (modalElement) {
        const bootstrapModal = window.bootstrap.Modal.getInstance(modalElement);
        bootstrapModal?.hide();
        setTimeout(() => {
          resetForm();
          goToPage(0);
        }, 300);
      }
      resetForm();
      
    } catch (error) {
      hideLoader();
      if (!error.response?.data?.Errors && !error.response?.data?.Message) {
        toast.error(error.message || "Desole, Une erreur inattendue s'est produite, Verifier la connexion et reesayer");
      }
    }
  };

  useEffect(() => {
    const modalElement = modalRef.current;
    if (modalElement) {
      const handleHidden = () => {
        resetForm();
        goToPage(0);
        if (onHide) onHide();
      };
      
      const handleShow = () => {
        if (!isEditMode && !initialAccountData) {
          resetForm();
          goToPage(0);
        }
      };
      
      modalElement.addEventListener('hidden.bs.modal', handleHidden);
      modalElement.addEventListener('show.bs.modal', handleShow);
      
      return () => {
        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
        modalElement.removeEventListener('show.bs.modal', handleShow);
      };
    }
  }, [onHide, resetForm, goToPage, isEditMode, initialAccountData]);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 0:
        return (
          <AccountTypePage
            control={control}
            errors={errors}
            backendErrors={backendErrors}
            register={register}
            domains={domains}
            role={role}
            managerDomain={selectedDomain}
            accountTypes={accountTypes}
            isFieldRequired={isFieldRequired}
            isUpdateMode={isUpdateMode}
            watch={watch}
            hasError={hasError}
            getErrorMessage={getErrorMessage}
          />
        );
        
      case 1:
        return (
          <div className="page-content">
            <div className="page-header mb-4">
              <h2 className="h4 mb-2">Informations Compte Administrative</h2>
              <p className="text-muted">
                Veillez activer si ce nouveau compte sera administrer
              </p>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    {...register("IsManagedAccount")}
                    id="IsManagedAccount"
                    checked={isManagedAccount}
                  />
                  <label className="form-check-label fw-bold" htmlFor="IsManagedAccount">
                    Ce compte est administré (nécessite un représentant légal)
                  </label>
                </div>
                <div className="form-text">
                  <i className="fas fa-info-circle me-1"></i>
                  Un compte administré ne possède pas d'email propre. Toutes les communications 
                  seront envoyées aux représentants légaux désignés.
                </div>
              </div>
            </div>

            {isManagedAccount && (
              <ManagedAccountSection
                control={control}
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                domainId={watch("domainId")}
                hasError={hasError}
                getErrorMessage={getErrorMessage}
                isUpdateMode={isUpdateMode}
                existingRepresentatives={initialAccountData?.representatives}
              />
            )}

            <PersonalInfoPage
              register={register}
              errors={errors}
              backendErrors={backendErrors}
              control={control}
              watch={watch}
              setValue={setValue}
              validEmail={validEmail}
              validDob={validDob}
              customErrors={customErrors}
              isFieldRequired={isFieldRequired}
              shouldShowField={shouldShowField}
              personalDisabled={personalDisabled}
              commonDisabled={commonDisabled}
              AccountFormService={AccountFormService}
              setValidEmail={setValidEmail}
              setValidDob={setValidDob}
              setCustomErrors={setCustomErrors}
              hasError={hasError}
              getErrorMessage={getErrorMessage}
              isManagedAccount={isManagedAccount}
            />
          </div>
        );
        
      case 2:
        return (
          <CompanyInfoPage
            register={register}
            errors={errors}
            backendErrors={backendErrors}
            watch={watch}
            setValue={setValue}
            customErrors={customErrors}
            isFieldRequired={isFieldRequired}
            shouldShowField={shouldShowField}
            companyDisabled={companyDisabled}
            commonDisabled={commonDisabled}
            isUpdateMode={isUpdateMode}
            AccountFormService={AccountFormService}
            setCustomErrors={setCustomErrors}
            isParticulier={isParticulier}
            hasError={hasError}
            getErrorMessage={getErrorMessage}
            existingFiles={existingFiles} 
            handleRemoveFile={handleRemoveFile}
          />
        );
        
      case 3:
        return (
          <DocumentsPage
            register={register}
            errors={errors}
            customErrors={customErrors}
            isFieldRequired={isFieldRequired}
            shouldShowField={shouldShowField}
            commonDisabled={commonDisabled}
            isUpdateMode={isUpdateMode}
            existingFiles={existingFiles}
            handleRemoveFile={handleRemoveFile}
            AccountFormService={AccountFormService}
            setCustomErrors={setCustomErrors}
            watch={watch}
            hasError={hasError}
            getErrorMessage={getErrorMessage}
            isManagedAccount={isManagedAccount} 
          />
        );
        
      case 4:
        return (
          <SummaryPage
            watch={watch}
            accountTypes={accountTypes}
            domains={domains}
            isParticulier={isParticulier}
            isEntrepriseType={isEntrepriseType}
            nonManagedAccounts={nonManagedAccounts} 
          />
        );
        
      default:
        return null;
    }
  };

  if (!user) {
    return <p>Chargement des informations utilisateur...</p>;
  }

  return (
    <>
      <div className="modal fade" id="newAccount" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true" ref={modalRef}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <FormHeader 
              isEditMode={isUpdateMode} 
              currentPage={currentPage} 
              totalPages={steps.length} 
            />
            
            <form className="modal-body" onSubmit={handleSubmit(onSubmit)}>
              <FormStepper
                currentPage={currentPage}
                visitedPages={visitedPages}
                goToPage={goToPage}
                steps={steps}
              />

              {Object.keys(backendErrors).length > 0 && (
                <div className="alert alert-danger">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-exclamation-triangle-fill me-2"></i>
                    <strong>Erreurs de validation :</strong>
                  </div>
                  <ul className="mb-0 mt-2">
                    {Object.entries(backendErrors).map(([field, messages]) => (
                      Array.isArray(messages) ? 
                        messages.map((msg, idx) => (
                          <li key={`${field}-${idx}`}>
                            <strong>{field}:</strong> {msg}
                          </li>
                        )) : 
                        <li key={field}>
                          <strong>{field}:</strong> {messages}
                        </li>
                    ))}
                  </ul>
                </div>
              )}

              {pageErrors[currentPage] && pageErrors[currentPage].length > 0 && (
                <div className="alert alert-danger">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-exclamation-triangle-fill me-2"></i>
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                  </div>
                  <ul className="mb-0 mt-2">
                    {pageErrors[currentPage].map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="container-fluid">
                {renderCurrentPage()}
              </div>

              <div className="modal-footer justify-content-between">
                <div>
                  {canGoPrev && (
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handlePrev}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Précédent
                    </button>
                  )}
                </div>
                
                <div>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary me-2" 
                    data-bs-dismiss="modal"
                    onClick={resetForm}
                  >
                    Annuler
                  </button>
                  
                  {isLastPage ? (
                    <button 
                      type="button"
                      className="btn btn-success"
                      onClick={handleFinalSubmit}
                      disabled={!isTypeChosen}
                    >
                      <i className="fas fa-check-circle me-2"></i>
                      {isUpdateMode ? "Modifier le Compte" : "Créer le Compte"}
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={!isTypeChosen}
                    >
                      Suivant
                      <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerForm;